import React, { useCallback, useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { IReturn } from "@/interfaces/Return";
import Return from "@/components/return/Return";
import ReturnSkeleton from "@/components/return/ReturnSkeleton";
import { Button } from "@/components/ui/button";
import { DEFAULT_PAGE_SIZE } from "@/constants/constants";
import { fetchReturnList } from "@/utils/return-list-fetching";
import { IS_RTO_QUERY_PARAM, canAccessRtoListFilter } from "@/utils/return-rto";

interface ReturnListProps {
  searchText: string;
  roles: string;
  isRefreshing: boolean;
  statusId: number;
  sellerName: string;
}

const ReturnList: React.FC<ReturnListProps> = ({
  searchText,
  roles,
  isRefreshing,
  sellerName,
  statusId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnList, setReturnList] = useState<IReturn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const latestFetchRef = useRef(0);
  const canUseRtoFilter = canAccessRtoListFilter(roles);
  const isRTO =
    canUseRtoFilter && searchParams.get(IS_RTO_QUERY_PARAM) === "true";

  const setIsRTO = (value: boolean) => {
    if (!canUseRtoFilter) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(IS_RTO_QUERY_PARAM, "true");
        } else {
          next.delete(IS_RTO_QUERY_PARAM);
        }
        return next;
      },
      { replace: true }
    );
  };

  const observer = useRef<IntersectionObserver>();
  const lastReturnElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const fetchReturns = useCallback(
    async (page: number, rtoFilter: boolean) => {
      const requestId = ++latestFetchRef.current;

      setIsLoading(true);
      if (page === 1) {
        setReturnList([]);
      }

      try {
        const newReturns = await fetchReturnList(roles, {
          keyword: parseInt(searchText) || 0,
          page_number: page,
          page_size: DEFAULT_PAGE_SIZE,
          statutId: statusId,
          vendeur_libelle: sellerName,
          ...(canUseRtoFilter && { isRTO: rtoFilter }),
        });

        if (requestId !== latestFetchRef.current) return;

        if (page === 1) {
          setReturnList(newReturns);
        } else {
          setReturnList((prev) => [...prev, ...newReturns]);
        }
        setHasMore(newReturns.length === DEFAULT_PAGE_SIZE);
      } finally {
        if (requestId === latestFetchRef.current) {
          setIsLoading(false);
        }
      }
    },
    [roles, searchText, statusId, sellerName, canUseRtoFilter]
  );

  useEffect(() => {
    setPageNumber(1);
    fetchReturns(1, isRTO);
  }, [isRefreshing, searchText, statusId, sellerName, canUseRtoFilter, isRTO, fetchReturns]);

  useEffect(() => {
    if (pageNumber > 1) {
      fetchReturns(pageNumber, isRTO);
    }
  }, [pageNumber, isRTO, fetchReturns]);

  return (
    <>
      {canUseRtoFilter && (
        <div className="flex flex-col gap-2 mb-4 w-full">
          <div className="flex gap-2 w-full">
            <Button
              className="w-full"
              variant={!isRTO ? "default" : "outline"}
              onClick={() => setIsRTO(false)}
            >
              Standard
            </Button>
            <Button
              className="w-full"
              variant={isRTO ? "default" : "outline"}
              onClick={() => setIsRTO(true)}
            >
              RTO
            </Button>
          </div>
        </div>
      )}

      {!isLoading && returnList.length === 0 && (
        <div className="text-center text-gray-500 font-sans my-2">
          Aucune tournée disponible
        </div>
      )}

      {returnList.map((item, index) => (
        <div
          key={item.numTournee}
          ref={
            index === returnList.length - 1 ? lastReturnElementRef : undefined
          }
        >
          <Return return_={item} />
        </div>
      ))}
      {isLoading &&
        Array.from({ length: 3 }).map((_, index) => (
          <ReturnSkeleton key={`loading-${index}`} />
        ))}
    </>
  );
};

export default ReturnList;
